import { OutboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import { getDayDifference } from '@utils';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { despatchLineCount } from '../../../utils/despatch';

interface ProofOfDeliverySummaryProps {
  selectedOutboundProcess: OutboundProcess;
  totalPODItemsQuantity: number | undefined;
  isPODDiscrepancy: boolean;
  podDiscrepancyAmount: number;
  isNegativePODDiscrepancy: boolean;
  hasProductLines?: boolean;
}

export function ProofOfDeliverySummary({
  selectedOutboundProcess,
  totalPODItemsQuantity,
  isPODDiscrepancy,
  podDiscrepancyAmount,
  isNegativePODDiscrepancy,
  hasProductLines = false,
}: ProofOfDeliverySummaryProps) {
  const lineCount = hasProductLines
    ? despatchLineCount(selectedOutboundProcess.despatchNote)
    : selectedOutboundProcess?.goodsReceivedNote?.skus.length;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { requiredTimeOfArrival } = selectedOutboundProcess.despatchNote!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { dateOfArrival } = selectedOutboundProcess.goodsReceivedNote!;

  const deliveryDayDifference = getDayDifference(
    requiredTimeOfArrival.dateTime as string,
    dateOfArrival.dateTime as string
  );

  const isLate = deliveryDayDifference < 0;

  return (
    <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {selectedOutboundProcess?.userBusinessName}
        </p>
        <p className='text-xs text-gray-400'>Received by</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {dayjs(
            selectedOutboundProcess?.goodsReceivedNote?.dateOfArrival.dateTime
          )
            .tz(
              selectedOutboundProcess?.goodsReceivedNote?.dateOfArrival.timeZone
            )
            .format('DD/MM/YY, H:mm')}
          {isLate && (
            <span className='text-red-600 ml-1'>
              (+{deliveryDayDifference.toString().substring(1)}{' '}
              {deliveryDayDifference.toString().substring(1) === '1'
                ? 'day'
                : 'days'}
              )
            </span>
          )}
        </p>
        <p className='text-xs text-gray-400'>POD date</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {selectedOutboundProcess?.goodsReceivedNote?.grnNoteNumber}
        </p>
        <p className='text-xs text-gray-400'>Proof of Delivery #</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {numberToLocale(lineCount ?? 0)}
        </p>
        <p className='text-xs text-gray-400'>Lines</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {numberToLocale(totalPODItemsQuantity ?? 0)}
          {isPODDiscrepancy && (
            <span
              className={
                isNegativePODDiscrepancy
                  ? 'text-red-600 ml-1'
                  : 'text-gray-900 ml-1'
              }
            >
              ({isNegativePODDiscrepancy ? '-' : '+'}
              {Math.abs(podDiscrepancyAmount)})
            </span>
          )}
        </p>
        <p className='text-xs text-gray-400'>
          {hasProductLines ? 'Total qty (Products)' : 'Total qty (Items)'}
        </p>
      </div>
      {selectedOutboundProcess.goodsReceivedNote?.comment && (
        <div className='col-span-2 pt-2'>
          <p className='text-sm text-gray-700'>
            {selectedOutboundProcess.goodsReceivedNote.comment}
          </p>
          <p className='text-xs text-gray-400'>Comment</p>
        </div>
      )}
    </div>
  );
}
